import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, showError } from '../../helpers';
import BillingDateCalendarV89, {
  serializeDateIntervalsV89,
} from '../../components/BillingDateCalendarV89';
import useBillingDateSelectionV97 from '../Billing/BillingDateSelectionStateV97';

const numberFormatter = new Intl.NumberFormat();

const formatTokensV8 = (value) => numberFormatter.format(Number(value || 0));

const formatTimestampV8 = (value) => {
  if (!value) return '-';
  return new Date(Number(value) * 1000).toLocaleString();
};

const UsageMetricV8 = ({ label, value, raw = false }) => (
  <div className='usage-audit-metric-v8'>
    <span>{label}</span>
    <strong>{raw ? value : formatTokensV8(value)}</strong>
  </div>
);

const UsageAuditV8 = ({
  admin = false,
  organizationId,
  members = [],
  initialUserId = '',
  currentUserId = 0,
  billingDay = 1,
}) => {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState(
    initialUserId ? 'token' : 'organization'
  );
  const {
    dates: selectedDates,
    setDates: setSelectedDates,
    refreshToday,
  } = useBillingDateSelectionV97(
    `${admin}:${organizationId}:${initialUserId}:${currentUserId}`,
    billingDay
  );
  const [userId, setUserId] = useState(
    initialUserId ? String(initialUserId) : ''
  );
  const [tokenId, setTokenId] = useState('');
  const [modelDraft, setModelDraft] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventTotal, setEventTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const requestRefV97 = useRef(0);
  const queryDatesRefV97 = useRef(null);
  const pageSize = 20;

  const endpointBase = admin
    ? `/api/billing/v8/admin/organizations/${organizationId}/usage`
    : '/api/billing/v8/organization/usage';
  const costEndpoint = admin
    ? `/api/billing/v8/admin/accounts/${organizationId}/date-selection-summary`
    : '/api/billing/v8/self/date-selection-summary';

  const memberOptions = useMemo(
    () =>
      (members || []).map((member) => ({
        key: member.user?.id,
        value: String(member.user?.id),
        text: member.user?.username || String(member.user?.id),
      })),
    [members]
  );

  useEffect(() => {
    const nextUserId = initialUserId ? String(initialUserId) : '';
    setUserId(nextUserId);
    setTokenId('');
    setGroupBy(nextUserId ? 'token' : 'organization');
    setPage(1);
  }, [initialUserId]);

  const dateIntervals = useMemo(
    () => serializeDateIntervalsV89(selectedDates),
    [selectedDates]
  );

  const buildQuery = useCallback(
    (withPage = false, targetPage = page) => {
      if (!dateIntervals) return '';
      const params = new URLSearchParams({
        date_intervals: dateIntervals,
        group_by: groupBy,
      });
      if (userId) params.set('user_id', userId);
      if (tokenId) params.set('token_id', tokenId);
      if (modelFilter) params.set('model', modelFilter);
      if (withPage) {
        params.set('page', String(targetPage));
        params.set('page_size', String(pageSize));
      }
      return params.toString();
    },
    [dateIntervals, groupBy, modelFilter, page, tokenId, userId]
  );

  const load = useCallback(
    async (targetPage = page) => {
      const request = ++requestRefV97.current;
      if (!dateIntervals) {
        setSummary(null);
        setCostSummary(null);
        setEvents([]);
        setEventTotal(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const summaryQuery = buildQuery(false, targetPage);
        const eventsQuery = buildQuery(true, targetPage);
        const [summaryResponse, eventsResponse, costResponse] =
          await Promise.all([
            API.get(`${endpointBase}/summary?${summaryQuery}`),
            API.get(`${endpointBase}/events?${eventsQuery}`),
            API.get(
              `${costEndpoint}?date_intervals=${encodeURIComponent(
                dateIntervals
              )}`
            ),
          ]);
        if (
          request !== requestRefV97.current ||
          !summaryResponse ||
          !eventsResponse ||
          !costResponse
        )
          return;
        if (!summaryResponse.data?.success)
          throw new Error(summaryResponse.data?.message);
        if (!eventsResponse.data?.success)
          throw new Error(eventsResponse.data?.message);
        if (!costResponse.data?.success)
          throw new Error(costResponse.data?.message);
        setSummary(summaryResponse.data.data || null);
        setCostSummary(costResponse.data.data || null);
        setEvents(eventsResponse.data.data?.events || []);
        setEventTotal(eventsResponse.data.data?.total || 0);
      } catch (error) {
        if (request === requestRefV97.current)
          showError(error.message || t('organization.usage.load_failed'));
      } finally {
        if (request === requestRefV97.current) setLoading(false);
      }
    },
    [buildQuery, costEndpoint, dateIntervals, endpointBase, page, t]
  );

  useEffect(() => {
    const targetPage = queryDatesRefV97.current === dateIntervals ? page : 1;
    queryDatesRefV97.current = dateIntervals;
    setPage(targetPage);
    load(targetPage);
    return () => {
      requestRefV97.current += 1;
    };
  }, [dateIntervals, load, page]);

  const applyFilters = () => {
    if (!dateIntervals) {
      showError(t('organization.usage.invalid_window'));
      return;
    }
    setModelFilter(modelDraft.trim());
    setPage(1);
  };

  const selectGroupBy = (nextGroup) => {
    setGroupBy(nextGroup);
    if (nextGroup === 'organization' && !initialUserId) {
      setUserId('');
      setTokenId('');
    } else if (nextGroup === 'account') {
      setTokenId('');
    } else if (nextGroup === 'token' && !admin) {
      setUserId(String(currentUserId));
      setTokenId('');
    }
    setPage(1);
  };

  const inspectAccount = (row) => {
    if (!admin && Number(row.user_id) !== Number(currentUserId)) return;
    setUserId(String(row.user_id));
    setTokenId('');
    setGroupBy('token');
    setPage(1);
  };

  const inspectToken = (row) => {
    setUserId(String(row.user_id));
    setTokenId(String(row.token_id));
    setPage(1);
  };

  const clearDrilldown = () => {
    setUserId(initialUserId ? String(initialUserId) : '');
    setTokenId('');
    setGroupBy(initialUserId ? 'token' : 'organization');
    setPage(1);
  };

  const totals = summary?.totals || {};
  const rows = summary?.rows || [];

  return (
    <div className='usage-audit-v8'>
      <div className='usage-audit-toolbar-v8'>
        <Button.Group size='small' className='usage-audit-group-control-v8'>
          {['organization', 'account', 'token'].map((value) => (
            <Button
              key={value}
              active={groupBy === value}
              onClick={() => selectGroupBy(value)}
            >
              <Icon
                name={
                  value === 'organization'
                    ? 'building outline'
                    : value === 'account'
                    ? 'user outline'
                    : 'key'
                }
              />
              {t(`organization.usage.group_${value}`)}
            </Button>
          ))}
        </Button.Group>
        <div className='usage-audit-filter-actions-v8'>
          {(userId || tokenId) && !initialUserId ? (
            <Button size='small' onClick={clearDrilldown}>
              <Icon name='remove filter' />
              {t('organization.usage.clear_drilldown')}
            </Button>
          ) : null}
          <Button primary size='small' onClick={applyFilters}>
            <Icon name='refresh' />
            {t('organization.usage.refresh')}
          </Button>
        </div>
      </div>

      <BillingDateCalendarV89
        billingDay={billingDay}
        value={selectedDates}
        onRefreshToday={refreshToday}
        onChange={(dates, selection) => {
          setSelectedDates(dates, selection);
          setPage(1);
        }}
        eligibilityEndpoint={costEndpoint.replace(
          '/date-selection-summary',
          '/eligibility-timeline'
        )}
      />

      <Form className='usage-audit-filters-v8'>
        <Form.Group widths='equal'>
          {memberOptions.length > 1 && !initialUserId ? (
            <Form.Field>
              <label>{t('organization.usage.account')}</label>
              <Dropdown
                fluid
                clearable
                search
                selection
                options={memberOptions}
                value={userId}
                onChange={(_, { value }) => {
                  const nextUserId = value || '';
                  setUserId(nextUserId);
                  setTokenId('');
                  if (
                    !admin &&
                    groupBy === 'token' &&
                    nextUserId !== String(currentUserId)
                  ) {
                    setGroupBy('account');
                  }
                  setPage(1);
                }}
              />
            </Form.Field>
          ) : null}
          <Form.Input
            label={t('organization.usage.model')}
            value={modelDraft}
            onChange={(_, { value }) => setModelDraft(value)}
          />
        </Form.Group>
      </Form>

      <div className='usage-audit-metrics-v8'>
        <UsageMetricV8
          label={t('organization.usage.effective_cost')}
          value={`USD ${costSummary?.effective_amount_usd || '0.00'}`}
          raw
        />
        <UsageMetricV8
          label={t('organization.usage.list_cost')}
          value={`USD ${costSummary?.list_amount_usd || '0.00'}`}
          raw
        />
        <UsageMetricV8
          label={t('organization.usage.discount_cost')}
          value={`USD ${costSummary?.discount_amount_usd || '0.00'}`}
          raw
        />
        <UsageMetricV8
          label={t('organization.usage.unresolved_cost')}
          value={formatTokensV8(costSummary?.unresolved_request_count)}
        />
        <UsageMetricV8
          label={t('organization.usage.requests')}
          value={totals.request_count}
        />
        <UsageMetricV8
          label={t('organization.usage.total_tokens')}
          value={totals.total_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.input_tokens')}
          value={totals.input_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.cached_input_tokens')}
          value={totals.cached_input_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.cache_creation_tokens')}
          value={totals.cache_creation_input_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.output_tokens')}
          value={totals.output_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.reasoning_tokens')}
          value={totals.reasoning_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.accepted_prediction_tokens')}
          value={totals.accepted_prediction_tokens}
        />
        <UsageMetricV8
          label={t('organization.usage.rejected_prediction_tokens')}
          value={totals.rejected_prediction_tokens}
        />
      </div>

      {loading && !summary ? (
        <Loader active inline='centered' />
      ) : rows.length === 0 ? (
        <Message info content={t('organization.usage.empty')} />
      ) : (
        <div className='management-table-scroll-v72' role='region' tabIndex={0}>
          <Table
            basic='very'
            compact
            unstackable
            className='management-table-v72 usage-audit-summary-table-v8'
          >
            <Table.Header>
              <Table.Row>
                {groupBy !== 'organization' ? (
                  <Table.HeaderCell>
                    {t('organization.usage.identity')}
                  </Table.HeaderCell>
                ) : null}
                <Table.HeaderCell>
                  {t('organization.usage.model')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.requests')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.total_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.input_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.cached_input_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.cache_creation_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.output_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.reasoning_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.accepted_prediction_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.rejected_prediction_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.last_request')}
                </Table.HeaderCell>
                {groupBy !== 'organization' ? (
                  <Table.HeaderCell>
                    {t('organization.table.actions')}
                  </Table.HeaderCell>
                ) : null}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row, index) => (
                <Table.Row
                  key={`${row.user_id || 0}:${row.token_id || 0}:${
                    row.model_name
                  }:${index}`}
                >
                  {groupBy !== 'organization' ? (
                    <Table.Cell>
                      <strong>
                        {groupBy === 'account'
                          ? row.username
                          : row.token_name || `#${row.token_id}`}
                      </strong>
                      {groupBy === 'token' ? (
                        <div className='organization-muted-v8'>
                          {row.username}
                        </div>
                      ) : null}
                    </Table.Cell>
                  ) : null}
                  <Table.Cell>{row.model_name}</Table.Cell>
                  <Table.Cell>{formatTokensV8(row.request_count)}</Table.Cell>
                  <Table.Cell>{formatTokensV8(row.total_tokens)}</Table.Cell>
                  <Table.Cell>{formatTokensV8(row.input_tokens)}</Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(row.cached_input_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(row.cache_creation_input_tokens)}
                  </Table.Cell>
                  <Table.Cell>{formatTokensV8(row.output_tokens)}</Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(row.reasoning_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(row.accepted_prediction_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(row.rejected_prediction_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTimestampV8(row.last_request_at)}
                  </Table.Cell>
                  {groupBy !== 'organization' ? (
                    <Table.Cell>
                      {groupBy !== 'account' ||
                      admin ||
                      Number(row.user_id) === Number(currentUserId) ? (
                        <Button
                          icon
                          labelPosition='left'
                          size='small'
                          onClick={() =>
                            groupBy === 'account'
                              ? inspectAccount(row)
                              : inspectToken(row)
                          }
                        >
                          <Icon
                            name={
                              groupBy === 'account'
                                ? 'key'
                                : 'list alternate outline'
                            }
                          />
                          {t(
                            groupBy === 'account'
                              ? 'organization.usage.view_tokens'
                              : 'organization.usage.view_requests'
                          )}
                        </Button>
                      ) : null}
                    </Table.Cell>
                  ) : null}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      <div className='usage-audit-events-header-v8'>
        <Header as='h3'>{t('organization.usage.request_details')}</Header>
        {tokenId ? <Label basic>{`Token #${tokenId}`}</Label> : null}
      </div>
      {events.length === 0 ? (
        <Message info content={t('organization.usage.events_empty')} />
      ) : (
        <div className='management-table-scroll-v72' role='region' tabIndex={0}>
          <Table
            basic='very'
            compact
            unstackable
            className='management-table-v72 usage-audit-events-table-v8'
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('organization.usage.time')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.request_id')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.account')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.token')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.model')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.input_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.cached_input_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.cache_creation_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.output_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.reasoning_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.accepted_prediction_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.rejected_prediction_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.total_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.request_cost')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.cost_status')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.outcome')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('organization.usage.source')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {events.map((event) => (
                <Table.Row key={event.request_id}>
                  <Table.Cell>{formatTimestampV8(event.created_at)}</Table.Cell>
                  <Table.Cell className='usage-audit-request-id-v8'>
                    {event.request_id}
                  </Table.Cell>
                  <Table.Cell>{event.username}</Table.Cell>
                  <Table.Cell>
                    {event.token_name ||
                      (event.token_id
                        ? `#${event.token_id}`
                        : t('organization.usage.token_private'))}
                  </Table.Cell>
                  <Table.Cell>{event.model_name}</Table.Cell>
                  <Table.Cell>{formatTokensV8(event.input_tokens)}</Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(event.cached_input_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(event.cache_creation_input_tokens)}
                  </Table.Cell>
                  <Table.Cell>{formatTokensV8(event.output_tokens)}</Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(event.reasoning_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(event.accepted_prediction_tokens)}
                  </Table.Cell>
                  <Table.Cell>
                    {formatTokensV8(event.rejected_prediction_tokens)}
                  </Table.Cell>
                  <Table.Cell>{formatTokensV8(event.total_tokens)}</Table.Cell>
                  <Table.Cell>
                    {event.billing_cost?.status === 'priced'
                      ? `USD ${event.billing_cost.effective_amount_usd}`
                      : '-'}
                    {event.billing_cost?.status === 'priced' ? (
                      <div className='organization-muted-v8'>
                        {t('organization.usage.list_cost_short', {
                          amount: event.billing_cost.list_amount_usd,
                        })}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      basic
                      color={
                        event.billing_cost?.status === 'priced'
                          ? event.billing_cost?.restated
                            ? 'blue'
                            : 'green'
                          : event.billing_cost?.status === 'unresolved'
                          ? 'orange'
                          : 'grey'
                      }
                    >
                      {t(
                        `organization.usage.cost_${
                          event.billing_cost?.status || 'unpriced'
                        }`
                      )}
                    </Label>
                    {event.billing_cost?.unresolved_reason ? (
                      <div className='organization-muted-v8'>
                        {event.billing_cost.unresolved_reason}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      basic
                      color={
                        event.outcome === 'success'
                          ? 'green'
                          : event.outcome === 'canceled'
                          ? 'grey'
                          : 'red'
                      }
                    >
                      {t(
                        `organization.usage.outcome_${
                          event.outcome || 'unknown'
                        }`
                      )}
                    </Label>
                    {event.http_status ? ` ${event.http_status}` : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {t(
                      `organization.usage.source_${
                        event.usage_source || 'unknown'
                      }`
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
      {eventTotal > pageSize ? (
        <div className='list-pagination-v8'>
          <Pagination
            activePage={page}
            totalPages={Math.ceil(eventTotal / pageSize)}
            onPageChange={(_, { activePage }) => setPage(Number(activePage))}
          />
        </div>
      ) : null}
    </div>
  );
};

export default UsageAuditV8;
